import type { PostingFrequency } from '../components/common/SocialFrequencyDisplay';

interface AnalysisResponse {
  analysis: {
    brandIdentity: {
      name?: string;
      mainLanguage?: string;
      industry?: string;
      bio?: string;
      voice?: string;
      contentTone?: string;
      imageStyle?: string;
      values?: string[];
      uniqueSellingPoints?: string[];
      keywords?: string[];
      visualIdentity?: {
        colorPalette?: {
          primary?: string[];
          secondary?: string[];
          accent?: string[];
          neutral?: string[];
          recommended?: {
            complementary?: string[];
            improvements?: string;
          };
        };
        typography?: {
          headings?: {
            h1?: string;
            h2?: string;
            h3?: string;
          };
          body?: {
            primary?: string;
            secondary?: string;
          };
          specialty?: {
            buttons?: string;
            captions?: string;
            navigation?: string;
          };
          fallbacks?: string[];
          recommendations?: {
            improvements?: string;
            hierarchy?: string;
          };
        };
      };
    };
    goToMarketStrategy?: {
      short?: string;
      marketScope?: string;
      targetMarkets?: string[];
      businessModel?: string;
      distributionType?: string;
      priorityMarkets?: string[];
      targetSegments?: string[];
      keyPersonaTypes?: string[];
    };
    contentStrategy: {
      themes?: string[];
      recommendedFormats?: string[];
      toneGuidelines?: string;
      recommendedPlatforms?: Array<{
        platform: string;
        postingFrequency: string;
      }>;
      contentGaps?: string[];
      engagementTactics?: string[];
      growthOpportunities?: string[];
      visualContentStrategy?: {
        colorUsage?: {
          socialMediaPosts?: string;
          callToActions?: string;
          backgrounds?: string;
          seasonalAdaptations?: string;
        };
        typographyGuidelines?: {
          socialMediaHeaders?: string;
          bodyText?: string;
          quotesTestimonials?: string;
          hashtagsKeywords?: string;
        };
        visualElementsToEmphasize?: string[];
        designConsistencyTips?: string[];
        brandExtensions?: {
          socialTemplates?: string;
          logoUsage?: string;
          imageFilters?: string;
        };
      };
    };
    overview: {
      summary?: string;
      takeaways?: string[];
    };
  };
  logo?: string;
  originalUrl?: string;
  visualAnalysis?: {
    branding?: {
      logo?: string;
    };
  };
}

/**
 * Maps API analysis response to the flat structure expected by BrandForm
 */
export function mapAnalysisToBrand(data: AnalysisResponse, fallbackUrl?: string) {
  const { analysis, logo, originalUrl } = data;
  const brandIdentity = analysis?.brandIdentity ?? {};
  const contentStrategy = analysis?.contentStrategy ?? {};
  const goToMarketStrategy = analysis?.goToMarketStrategy ?? {};
  const overview = analysis?.overview ?? {};
  
  // Use originalUrl from data, or fallback to provided URL
  const websiteUrl = originalUrl || fallbackUrl || "";

  // Utility function to normalize industry from display format to ID
  const normalizeIndustry = (label: string | undefined) => {
    if (!label) return "advertising-marketing";
    const lower = label.toLowerCase();
    if (lower.includes("automotive")) return "automotive";
    if (lower.includes("education")) return "education";
    if (lower.includes("healthcare")) return "healthcare";
    if (lower.includes("technology") || lower.includes("information technology")) return "information-technology";
    if (lower.includes("saas") || lower.includes("software as a service")) return "saas";
    if (lower.includes("finance") || lower.includes("banking")) return "banking-financial";
    if (lower.includes("retail")) return "retail";
    if (lower.includes("food") || lower.includes("beverage")) return "food-beverage";
    if (lower.includes("travel") || lower.includes("hospitality") || lower.includes("tourism")) return "hospitality-tourism";
    if (lower.includes("real estate")) return "real-estate";
    if (lower.includes("nonprofit") || lower.includes("non-profit")) return "non-profit";
    if (lower.includes("government")) return "government";
    if (lower.includes("entertainment") || lower.includes("media")) return "entertainment-media";
    if (lower.includes("sports")) return "sports";
    if (lower.includes("fashion") || lower.includes("apparel")) return "fashion-apparel";
    if (lower.includes("gaming")) return "gaming";
    if (lower.includes("ecommerce") || lower.includes("e-commerce")) return "ecommerce";
    return "advertising-marketing"; // default fallback
  };

  // Normalize content tone
  const normalizeContentTone = (tone: string | undefined) => {
    if (!tone) return "professional";
    const lower = tone.toLowerCase();
    if (lower.includes("informative") || lower.includes("informatif")) return "informative";
    if (lower.includes("friendly") || lower.includes("amical")) return "friendly";
    if (lower.includes("professional") || lower.includes("professionnel")) return "professional";
    if (lower.includes("casual") || lower.includes("décontracté")) return "casual";
    if (lower.includes("authoritative") || lower.includes("autoritaire")) return "authoritative";
    if (lower.includes("inspiring") || lower.includes("inspirant")) return "inspiring";
    if (lower.includes("playful") || lower.includes("ludique")) return "playful";
    if (lower.includes("empathetic") || lower.includes("empathique")) return "empathetic";
    if (lower.includes("actionable") || lower.includes("actionnable")) return "actionable";
    return "professional"; // default fallback
  };

  // Normalize image style
  const normalizeImageStyle = (style: string | undefined) => {
    if (!style) return "auto";
    const lower = style.toLowerCase();
    if (lower.includes("realistic") || lower.includes("réaliste")) return "realistic";
    if (lower.includes("design")) return "design";
    if (lower.includes("3d")) return "3d";
    if (lower.includes("artistic") || lower.includes("artistique")) return "artistic";
    if (lower.includes("modern") || lower.includes("moderne")) return "modern";
    if (lower.includes("ad") || lower.includes("advertisement") || lower.includes("publicité")) return "ad";
    if (lower.includes("auto") || lower.includes("automatique")) return "auto";
    return "auto"; // default fallback
  };

  return {
    // Brand Identity
    id: "",
    name: brandIdentity.name ?? "",
    description: brandIdentity.bio ?? "",
    logo: logo || data.visualAnalysis?.branding?.logo || "",
    website: websiteUrl,
    industryId: normalizeIndustry(brandIdentity.industry),
    values: brandIdentity.values ?? [],
    keywords: brandIdentity.keywords ?? [],
    languages: [brandIdentity.mainLanguage ?? "en"],
    contentTone: normalizeContentTone(contentStrategy.toneGuidelines || brandIdentity.contentTone),
    imageStyle: normalizeImageStyle(brandIdentity.imageStyle),
    voice: brandIdentity.voice ?? "",
    uniqueSellingPoints: brandIdentity.uniqueSellingPoints ?? [],

    // Content Strategy
    recommendedFormats: contentStrategy.recommendedFormats ?? [],
    socialMedias: (contentStrategy.recommendedPlatforms ?? []).map(platform => ({
      platform: platform.platform,
      frequency: (platform.postingFrequency as PostingFrequency) || "weekly"
    })),
    themes: contentStrategy.themes ?? [],
    contentGaps: contentStrategy.contentGaps ?? [],
    engagementTactics: contentStrategy.engagementTactics ?? [],
    growthOpportunities: contentStrategy.growthOpportunities ?? [],

    // Summary
    summary: {
      recap: overview.summary ?? "",
      takeaways: overview.takeaways ?? []
    },

    // Visual Identity (the key part that was missing!)
    visualIdentity: brandIdentity.visualIdentity ?? {
      colorPalette: {
        primary: [],
        secondary: [],
        accent: [],
        neutral: [],
        recommended: {
          complementary: [],
          improvements: ""
        }
      },
      typography: {
        headings: { h1: "", h2: "", h3: "" },
        body: { primary: "", secondary: "" },
        specialty: { buttons: "", captions: "", navigation: "" },
        fallbacks: [],
        recommendations: { improvements: "", hierarchy: "" }
      }
    },

    // Visual Content Strategy
    visualContentStrategy: contentStrategy.visualContentStrategy ?? {
      colorUsage: {
        socialMediaPosts: "",
        callToActions: "",
        backgrounds: "",
        seasonalAdaptations: ""
      },
      typographyGuidelines: {
        socialMediaHeaders: "",
        bodyText: "",
        quotesTestimonials: "",
        hashtagsKeywords: ""
      },
      visualElementsToEmphasize: [],
      designConsistencyTips: [],
      brandExtensions: {
        socialTemplates: "",
        logoUsage: "",
        imageFilters: ""
      }
    },

    // Go-To-Market Strategy (NEW!)
    goToMarketStrategy: {
      short: goToMarketStrategy.short ?? "",
      targetSegments: goToMarketStrategy.targetSegments ?? [],
      keyPersonaTypes: goToMarketStrategy.keyPersonaTypes ?? [],
      marketScope: goToMarketStrategy.marketScope ?? "local",
      priorityMarkets: goToMarketStrategy.priorityMarkets ?? goToMarketStrategy.targetMarkets ?? [],
      businessModel: goToMarketStrategy.businessModel ?? "B2C",
      distributionType: goToMarketStrategy.distributionType ?? "digital"
    }
  };
} 